import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import ListingCard from "@/components/ListingCard";
import ScrollBanner from "@/components/ScrollBanner";
import MobileHero from "@/components/MobileHero";
import TrustCounters from "@/components/TrustCounters";
import useIsMobile from "@/hooks/useIsMobile";
import { BadgeCheck, Boxes, ArrowRight, Lock, Zap } from "lucide-react";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [cats, setCats] = useState([]);
  const [designers, setDesigners] = useState([]);
  const isMobile = useIsMobile();

  useEffect(() => {
    api.get("/listings", { params: { featured: true } }).then((r) => {
      const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      setFeatured(items.slice(0, 8));
    });
    api.get("/categories").then((r) => setCats(r.data.filter((c) => !c.parent_id)));
    api.get("/designers").then((r) => setDesigners(r.data.filter((d) => d.rating_count > 0).slice(0, 4)));
  }, []);

  return (
    <div>
      {/* Mobile: lightweight static hero (no WebGL, no 8MB GLB). Desktop: full cinematic scroll banner. */}
      {isMobile ? <MobileHero /> : <ScrollBanner />}

      {/* TRUST COUNTERS (animated count-up) */}
      <TrustCounters />

      {/* CATEGORIES */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="font-display font-bold text-2xl sm:text-3xl">Browse by category</h2>
            <p className="text-muted-foreground mt-1">Every jewellery vertical, structured for manufacturers worldwide.</p>
          </div>
          <Link to="/marketplace" className="hidden sm:flex items-center gap-1 text-sm text-primary font-medium hover:gap-2 transition-all">
            View all <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {cats.slice(0, 14).map((c) => (
            <Link
              key={c.id}
              to={`/marketplace?category=${c.id}`}
              data-testid={`home-cat-${c.id}`}
              className="group border border-border rounded-md p-4 bg-card hover:border-primary hover:-translate-y-0.5 transition-all text-center"
            >
              <Boxes className="h-6 w-6 mx-auto text-muted-foreground group-hover:text-primary transition-colors mb-2" strokeWidth={1.5} />
              <span className="text-xs font-medium leading-tight block">{c.name}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 pb-8">
        <div className="flex items-end justify-between mb-8">
          <h2 className="font-display font-bold text-2xl sm:text-3xl">Trending designs</h2>
          <Link to="/marketplace?sort=trending" className="text-sm text-primary font-medium hover:underline">See more</Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
          {featured.map((l) => <ListingCard key={l.id} listing={l} />)}
        </div>
      </section>

      {/* HOW */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 py-16">
        <div className="grid md:grid-cols-3 gap-5">
          {[
            { icon: Boxes, t: "Inspect in 3D", d: "Orbit, zoom and toggle wireframe or metal finish on every file — before you spend a cent." },
            { icon: Lock, t: "Escrow-protected", d: "Payments are held for 48 hours. Raise a dispute and funds freeze instantly until resolved." },
            { icon: Zap, t: "Instant delivery", d: "Buy and download the original .3dm / .stl immediately via secure, authenticated links." },
          ].map((f) => (
            <div key={f.t} className="border border-border rounded-md p-6 bg-card">
              <div className="h-10 w-10 rounded-md bg-primary/10 text-primary flex items-center justify-center mb-4">
                <f.icon className="h-5 w-5" />
              </div>
              <h3 className="font-display font-semibold text-lg mb-1">{f.t}</h3>
              <p className="text-sm text-muted-foreground">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* DESIGNERS */}
      {designers.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-4 sm:px-6 pb-4">
          <h2 className="font-display font-bold text-2xl sm:text-3xl mb-8">Top-rated designers</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {designers.map((d) => (
              <Link key={d.id} to={`/designer/${d.id}`} className="border border-border rounded-md p-5 bg-card hover:border-primary transition-colors flex items-center gap-3">
                <div className="h-12 w-12 rounded-full bg-primary/15 text-primary flex items-center justify-center font-display font-bold text-lg">
                  {d.name?.[0]}
                </div>
                <div>
                  <p className="font-semibold text-sm flex items-center gap-1">{d.name} {d.verified && <BadgeCheck className="h-4 w-4 text-primary" />}</p>
                  <p className="font-mono text-xs text-muted-foreground">★ {d.rating} · {d.rating_count} reviews</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* CTA — CAD designers + CAM vendors, side by side */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 py-16 grid lg:grid-cols-2 gap-6">
        <div className="rounded-lg border border-border bg-slate-950 text-white p-8 lg:p-12 relative overflow-hidden">
          <div className="absolute inset-0 mesh-grid opacity-30" />
          <div className="relative">
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-blue-300 mb-3">For designers</p>
            <h2 className="font-display font-extrabold text-2xl lg:text-3xl leading-tight">Are you a CAD designer?</h2>
            <p className="mt-3 text-sm text-slate-300 leading-relaxed">Sell your jewellery designs to manufacturers around the world. Set your price, keep control, and get paid securely with our 48-hour escrow release.</p>
            <Link to="/register" className="inline-flex items-center gap-2 mt-6 bg-primary text-primary-foreground rounded-md px-5 py-2.5 font-semibold text-sm hover:bg-primary/90 transition-colors" data-testid="cta-become-seller">
              Start selling <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>

        <div className="rounded-lg border border-border p-8 lg:p-12 relative overflow-hidden text-white" style={{ background: "linear-gradient(135deg, #052e3f 0%, #084a68 55%, #0b6787 100%)" }}>
          <div
            aria-hidden
            className="absolute inset-0 opacity-40"
            style={{
              backgroundImage: "linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)",
              backgroundSize: "36px 36px",
            }}
          />
          <div className="relative">
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-emerald-300 mb-3 flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" /> Onboarding now — Free
            </p>
            <h2 className="font-display font-extrabold text-2xl lg:text-3xl leading-tight">Do you own 3D printers?</h2>
            <p className="mt-3 text-sm text-slate-200 leading-relaxed">Partner with Jeweltix as a CAM Vendor. Fill your idle print hours with paid jewellery print jobs — payments upfront, escrow-protected, STL files only.</p>
            <Link to="/vendor/apply" className="inline-flex items-center gap-2 mt-6 bg-emerald-500 hover:bg-emerald-400 text-white rounded-md px-5 py-2.5 font-semibold text-sm transition-colors" data-testid="cta-become-vendor">
              Partner with us <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
